import { rotate, unitFromAngle } from '../../shared/angle';
import { distance, type Vec2 } from '../../shared/vec';
import { MAIN_STAGE } from '../main-stage';
import type { ObstacleDefinition, SetDefinition } from '../types';
import { denserTiers, inCorners } from './stage-kit';

const DOME_TICKS_PER_BEAT = 18;

interface CrownShape {
  center: Vec2;
  radius: number;
  posts: number;
  postRadius: number;
  openEvery: number;
}

interface ArmsShape {
  center: Vec2;
  giants: number;
  firstGiantRadians: number;
  curve: readonly [Vec2, Vec2, Vec2, Vec2];
  stoneRadius: number;
  gap: number;
  stonesPerSide: number;
}

function crownObstacles(shape: CrownShape): ObstacleDefinition[] {
  const obstacles: ObstacleDefinition[] = [];
  for (let i = 1; i < shape.posts; i++) {
    if (i % shape.openEvery === 0) {
      continue;
    }
    const unit = unitFromAngle((i * 2 * Math.PI) / shape.posts);
    obstacles.push({
      x: Math.round(shape.center.x + shape.radius * unit.x),
      y: Math.round(shape.center.y + shape.radius * unit.y),
      radius: shape.postRadius,
    });
  }
  return obstacles;
}

const ARM_SAMPLES = 400;

function bezierAt([p0, p1, p2, p3]: ArmsShape['curve'], t: number): Vec2 {
  const u = 1 - t;
  const a = u * u * u;
  const b = 3 * u * u * t;
  const c = 3 * u * t * t;
  const d = t * t * t;
  return {
    x: a * p0.x + b * p1.x + c * p2.x + d * p3.x,
    y: a * p0.y + b * p1.y + c * p2.y + d * p3.y,
  };
}

function armStones(shape: ArmsShape): Vec2[] {
  const chord = 2 * shape.stoneRadius + shape.gap;
  const curve = Array.from({ length: ARM_SAMPLES + 1 }, (_, i) =>
    bezierAt(shape.curve, i / ARM_SAMPLES),
  );
  const stones: Vec2[] = [];
  let last: Vec2 = { x: shape.curve[0].x, y: 0 };
  let next = 1;
  while (stones.length < shape.stonesPerSide) {
    const from = curve[next - 1];
    const to = curve[next];
    if (from === undefined || to === undefined) {
      throw new Error('arm curve is too short for its stones');
    }
    const before = distance(from, last);
    const after = distance(to, last);
    if (after < chord) {
      next++;
      continue;
    }
    const along = (chord - before) / (after - before);
    last = { x: from.x + (to.x - from.x) * along, y: from.y + (to.y - from.y) * along };
    stones.push(last);
  }
  return stones;
}

function armsObstacles(shape: ArmsShape): ObstacleDefinition[] {
  const stones = armStones(shape);
  const axisStone = { x: shape.curve[0].x, y: 0 };
  const obstacles: ObstacleDefinition[] = [];
  for (let giant = 0; giant < shape.giants; giant++) {
    const radians = shape.firstGiantRadians + (giant * 2 * Math.PI) / shape.giants;
    const place = (point: Vec2): ObstacleDefinition => {
      const world = rotate(point, radians);
      return {
        x: Math.round(shape.center.x + world.x),
        y: Math.round(shape.center.y + world.y),
        radius: shape.stoneRadius,
      };
    };
    obstacles.push(place(axisStone));
    for (const side of [-1, 1]) {
      for (const stone of stones) {
        obstacles.push(place({ x: stone.x, y: side * stone.y }));
      }
    }
  }
  return obstacles;
}

// Who passes between two posts or stones: docs/full/scenes.md, « Game design ».
export const DOME_SCALE = 10 / 9;
const DOME_CENTER = { x: 800, y: 500 };

const DOME_OBSTACLES: readonly ObstacleDefinition[] = [
  ...crownObstacles({
    center: DOME_CENTER,
    radius: 330,
    posts: 24,
    postRadius: 24,
    openEvery: 6,
  }),
  ...armsObstacles({
    center: DOME_CENTER,
    giants: 4,
    firstGiantRadians: (-3 * Math.PI) / 4,
    curve: [
      { x: 398 * DOME_SCALE, y: -32 * DOME_SCALE },
      { x: 432 * DOME_SCALE, y: -120 * DOME_SCALE },
      { x: 392 * DOME_SCALE, y: -238 * DOME_SCALE },
      { x: 297 * DOME_SCALE, y: -297 * DOME_SCALE },
    ],
    stoneRadius: 18,
    gap: 38,
    stonesPerSide: 4,
  }),
];

export const DOME: SetDefinition = {
  ...MAIN_STAGE,
  tiers: denserTiers(1.4),
  id: 'dome',
  trackIds: [
    'sous-la-coupole',
    'route-de-la-soie',
    'dub-des-champignons',
    'la-ceremonie',
    'mandala-de-feu',
  ],
  name: 'Le Dome',
  style: 'Downtempo, dub',
  ticksPerBeat: DOME_TICKS_PER_BEAT,
  decor: 'dome',
  obstacles: DOME_OBSTACLES,
  acoustics: { reverbSeconds: 3.6, wet: 0.3 },
  speakers: inCorners(MAIN_STAGE.speakers),
};
